import { ServiceWithRFQID } from '../Service'
import { ModelInvoice, ModelInvoiceDetail } from './models'
import { ServiceRFQ3s } from '@/services/RFQ3s'
import { getUnavailableStockStatusID } from '@/utils/shared'
import { ServiceStocks } from '@/services/Stocks'
import { ServiceRFQs } from '@/services/RFQs'
import { api } from '../api'
import { equals, makeFilter, mergeFilters, createParams } from '@/utils/dynamic'
import { ServiceCustomerPOFollowUp } from '@/services/CustomerPOFollowUp'

export const ENDPOINTS = {
  GET: 'Invoices/Get',
  GET_DYNAMIC: 'Invoices/GetAllDynamic',
  POST: 'Invoices/Create',
  PATCH: 'Invoices/Patch',
  PUT: 'Invoices/Put',
  DELETE: 'Invoices/Delete',
  INVOICE_DETAILS_CREATE_DYNAMIC: 'InvoiceDetails/CreateDynamic',
  INVOICE_CREATE_DETAIL: 'InvoiceDetails/Create',
  GET_INVOICE_DETAILS: 'InvoiceDetails/GetAllDynamic'
}

class ModuleServiceInvoice extends ServiceWithRFQID {
  async checkUniqueInvoiceNumber ({ invoiceNumber }) {
    return this.getAllDynamic({ ...createParams({ filter: `invoiceNumber == "${invoiceNumber}"` }) })
      .then(({ data }) => {
        return data.value.length === 0
      })
  }

  async creatInvoiceDetailsDynamic ({ invoiceID, rfqid }) {
    rfqid = encodeURIComponent(rfqid)
    return api
      .post(`${ENDPOINTS.INVOICE_DETAILS_CREATE_DYNAMIC}/${invoiceID}/${rfqid}`)
  }

  async getInvoiceDetails ({ invoiceNumber, rfqid }) {
    if (!invoiceNumber) {
      return Promise.reject(new Error('invoiceNumber is required'))
    }
    if (!rfqid) {
      return Promise.reject(new Error('rfqid is required'))
    }
    const params = createParams({
      select: 'id, invoiceNumber, rfQ3ID, rfqid',
      filter: mergeFilters(
        makeFilter('invoiceNumber', invoiceNumber, equals),
        makeFilter('rfqid', rfqid, equals)
      ).join('&&')
    })
    return api
      .get(`${ENDPOINTS.GET_INVOICE_DETAILS}`, { params })
      .then((response) => {
        return {
          data: response.data.value
        }
      })
  }

  async getInvoiceDetailsRFQ3 ({ invoiceNumber, rfqid, params = {} }) {
    const { data } = await this.getInvoiceDetails({ invoiceNumber, rfqid })

    if (data.length === 0) {
      return { data: { value: [] } }
    }

    const result = await Promise.all(data.map(({ rfQ3ID }) => {
      return ServiceRFQ3s.getDynamic(rfQ3ID, params)
    }))

    return {
      data: {
        value: result.map(({ data }) => data)
      }
    }
  }

  async createDetail ({ rfqid, rfq3ID, invoiceNumber }) {
    const _data = {
      rfqid: rfqid,
      rfQ3ID: rfq3ID,
      invoiceNumber
    }
    if (!_data.rfqid) {
      return Promise.reject(new Error('rfqid is required'))
    }
    if (!_data.rfQ3ID) {
      return Promise.reject(new Error('rfq3ID is required'))
    }
    if (!_data.invoiceNumber) {
      return Promise.reject(new Error('invoiceNumber is required'))
    }
    return api.post(`${ENDPOINTS.INVOICE_CREATE_DETAIL}`, _data)
  }

  async _updateStock ({ invoiceNumber, rfqid, data }) {
    const params = {
      select: 'id,rfqid,stockID,stock.statusID as stockStatusID',
      filter: `stock.statusID != ${getUnavailableStockStatusID()}`
    }
    const { data: resultRFQ3s } = await this.getInvoiceDetailsRFQ3({ invoiceNumber, rfqid, params })

    const stockList = resultRFQ3s.value.filter(({ stockID }) => !!stockID)

    if (stockList.length === 0) {
      throw new Error('the Stock list is empty')
    }

    return Promise
      .all(stockList
        .map(({ stockID }) => {
          return ServiceStocks.patch({ id: stockID, ...data })
        })
      )
  }

  async updateStockSales ({ invoiceNumber, rfqid }) {
    const { data: dataRFQ } = await ServiceRFQs.getByRFQID(rfqid, { select: 'rfqid,customerCompanyID,customerCompany.name as customerCompanyName,po' })
    const data = {
      statusID: 2,
      statusInd: dataRFQ.customerCompanyName,
      warehouseLocationDescription: String(dataRFQ.po || '').substring(0, 10)
    }
    return this._updateStock({ invoiceNumber, rfqid, data })
  }

  async updateStockExch ({ invoiceNumber, rfqid }) {
    const { data: dataRFQ } = await ServiceRFQs.getByRFQID(rfqid, { select: 'rfqid,customerCompanyID,customerCompany.name as customerCompanyName,po' })
    const data = {
      stockLocationID: 4,
      externalLocationCompanyID: dataRFQ.customerCompanyID,
      statusInd: dataRFQ.customerCompanyName,
      warehouseLocationDescription: String(dataRFQ.po || '').substring(0, 10),
      warehouseLocationID: null
    }
    return this._updateStock({ invoiceNumber, rfqid, data })
  }

  async updateUnitShippedInFollowUp ({ invoiceNumber, rfqid }) {
    const { data: invoiceDetails } = await this.getInvoiceDetails({ invoiceNumber, rfqid })

    return Promise.all(invoiceDetails.map(async (invoiceDetail) => {
      const { data: { value: followUps } } = await ServiceCustomerPOFollowUp.getAllDynamic({
        filter: mergeFilters(makeFilter('rfQ3ID', invoiceDetail.rfQ3ID, equals)).join('&&'),
        select: ['id'].join(',')
      })
      return Promise.all(followUps.map((followUp) => {
        return ServiceCustomerPOFollowUp.patch({ ...followUp, unitShipped: true })
      }))
    }))
  }
}

export const ServiceInvoice = new ModuleServiceInvoice({
  apiGet: ENDPOINTS.GET,
  apiGetDynamic: ENDPOINTS.GET_DYNAMIC,
  apiPost: ENDPOINTS.POST,
  apiPatch: ENDPOINTS.PATCH,
  apiPut: ENDPOINTS.PUT,
  apiDelete: ENDPOINTS.DELETE
})

export {
  ModelInvoice,
  ModelInvoiceDetail
}
